import { useEffect, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  createCertificationSchema,
  CERTIFICATION_TYPE_LABELS,
  type CreateCertificationInput,
  type WorkerCertification,
} from "@crewledger/shared/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CertificationFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateCertificationInput) => Promise<void>;
  initial?: WorkerCertification | null;
  loading?: boolean;
}

export function CertificationFormModal({
  open,
  onClose,
  onSubmit,
  initial,
  loading = false,
}: CertificationFormModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const form = useForm<CreateCertificationInput>({
    resolver: zodResolver(createCertificationSchema),
    defaultValues: {
      certificationType: "FORKLIFT",
      certificationName: "",
      certificationCode: "",
      issuedAt: "",
      expiresAt: "",
      documentUrl: "",
      status: "ACTIVE",
      notes: "",
    },
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      form.reset({
        certificationType: initial.certificationType,
        certificationName: initial.certificationName,
        certificationCode: initial.certificationCode ?? "",
        issuedAt: initial.issuedAt ?? "",
        expiresAt: initial.expiresAt ?? "",
        documentUrl: initial.documentUrl ?? "",
        status: initial.status,
        notes: initial.notes ?? "",
      });
    } else {
      form.reset({
        certificationType: "FORKLIFT",
        certificationName: "",
        certificationCode: "",
        issuedAt: "",
        expiresAt: "",
        documentUrl: "",
        status: "ACTIVE",
        notes: "",
      });
    }
  }, [open, initial, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const payload: CreateCertificationInput = {
      ...values,
      certificationCode: values.certificationCode?.trim() || null,
      issuedAt: values.issuedAt?.trim() || null,
      expiresAt: values.expiresAt?.trim() || null,
      documentUrl: values.documentUrl?.trim() || null,
      notes: values.notes?.trim() || null,
    };
    await onSubmit(payload);
  });

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-auto w-full max-w-lg rounded-xl border-0 bg-card-white p-0 shadow-[var(--shadow-elevated)] backdrop:bg-navy-deep/50 open:animate-in"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <form onSubmit={(e) => void handleSubmit(e)} className="p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-navy-deep">
              {initial ? "Edit certification" : "Add certification"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Track operator licenses and safety credentials.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="certificationType">Type</Label>
            <select
              id="certificationType"
              {...form.register("certificationType")}
              className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
            >
              {Object.entries(CERTIFICATION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="certificationName">Certification name</Label>
            <Input id="certificationName" {...form.register("certificationName")} />
            {form.formState.errors.certificationName ? (
              <p className="text-xs text-red-danger">
                {form.formState.errors.certificationName.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="certificationCode">Code / ID</Label>
            <Input id="certificationCode" {...form.register("certificationCode")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              {...form.register("status")}
              className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
            >
              <option value="ACTIVE">Active</option>
              <option value="PENDING">Pending</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="issuedAt">Issued</Label>
            <Input id="issuedAt" type="date" {...form.register("issuedAt")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expiresAt">Expires</Label>
            <Input id="expiresAt" type="date" {...form.register("expiresAt")} />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="documentUrl">Document URL</Label>
            <Input
              id="documentUrl"
              type="url"
              placeholder="https://…"
              {...form.register("documentUrl")}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="notes">Notes</Label>
            <textarea
              id="notes"
              {...form.register("notes")}
              rows={3}
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading || form.formState.isSubmitting}>
            {loading || form.formState.isSubmitting ? "Saving…" : initial ? "Save changes" : "Add"}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
